import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowDownToLine,
  faBoxOpen,
  faFile,
} from "@fortawesome/pro-light-svg-icons";
import { faReply } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IconButton from "@mui/material/IconButton/IconButton";
import { i18n } from "i18next";
import React, { MutableRefObject, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import {
  DownloadFileProp,
  getFilePreviewUrl,
  getFilePreviewUrlV1,
  getTransferDownloadUrl,
} from "../../api";
import Avatar from "../../components/avatar";
import ExceptionDialog from "../../components/errorDialog";
import {
  FieldsPrefillData,
  FormPrefillType,
  MalwareStatus,
  SkpTransferFile,
  Transfer,
  TransferFileObject,
  TransferType,
} from "../../entities";
import { useWindowDimensions } from "../../hooks/useWindowDimensions";
import { cssVar } from "../../utils/cssVar";
import {
  getHumanReadableFileSize,
  padIfSingleDigit,
  padTransferExpirationMonth,
} from "../../utils/units";
import PreviewPage from "../preview/Preview";
import { Tooltip } from "@mui/material";
import { Spacer } from "../../components/layout";
import { DownloadAllButtonIcon } from "../../components/downloadAllButtonIcon";
import { encodeBase64UrlJson } from "../../utils/url";
import { constants } from "../../utils/constants";
import { FileIcon } from "../../utils/fileIcon";
import { useSkpEnvironment } from "../../providers/environment";
import { formatDateTime } from "../../utils/date";
import { featureFlags } from "../../utils/featureFlags";
import {
  resolveTransferDownloadCta,
  TransferDownloadCta,
} from "./transferDownloadPolicy";

type QuarantineStats = {
  infectedCount: number;
  errorCount: number;
  unknownCount: number;
};

function getFileQuarantineTooltip(
  status: MalwareStatus,
  t: (key: string) => string
): string {
  switch (status) {
    case MalwareStatus.infected:
      return t(
        `File is not available for download because it is classified as infected`
      );
    case MalwareStatus.error:
      return t(
        `File is not available for download because the scan was completed with an error`
      );
    case MalwareStatus.unknown:
      return t(
        `File is not available for download because it was not scanned for malware`
      );
    default:
      return "";
  }
}

function getZipTooltip(
  stats: QuarantineStats,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (stats.infectedCount > 0) {
    return t(
      `Downloads all files not classified as infected in a .zip file. {{infectedCount}} infected files were removed.`,
      { infectedCount: stats.infectedCount }
    );
  }
  if (stats.errorCount > 0) {
    return t(
      `Downloads all files that were successfully scanned in a .zip file. {{errorCount}} files that returned a scan error were removed.`,
      { errorCount: stats.errorCount }
    );
  }
  if (stats.unknownCount > 0) {
    return t(
      `Downloads all scanned files in a .zip file. {{unknownCount}} unscanned files were removed.`,
      { unknownCount: stats.unknownCount }
    );
  }
  return "";
}

// TODO refactor in a separate MR
// maybe a transfer provider is a good idea

/**
 * Converts TransferFileObject to SkpTransferFile format
 */
/**
 * Sender-side portal origin from transfer file URLs. Recipients may load the transfer
 * UI on a different host than the sender's mailbox; object URLs point at the API host
 * that issued the transfer (same origin we need for `/mailbox/{uid}`).
 */
function getSenderMailboxOriginFromTransfer(transfer: Transfer): string {
  for (const obj of transfer.objects) {
    const raw = obj.url as URL | string;
    if (raw == null || raw === "") continue;
    try {
      const parsed = raw instanceof URL ? raw : new URL(String(raw));
      if (parsed.origin) return parsed.origin;
    } catch {
      continue;
    }
  }
  return window.location.origin;
}

function mapTransferFileObjectToSkpTransferFile(
  fileObject: TransferFileObject
): SkpTransferFile {
  return {
    id: fileObject.uid,
    name: fileObject.name,
    size: fileObject.contentLength,
    objectId: fileObject.uid,
    url: fileObject.url,
    hashsum: fileObject.hashsum,
    malware: {
      status: fileObject.malwareStatus,
      text: fileObject.malwareText,
    },
    downloadCounter: fileObject.downloadCounter,
    mimeType: fileObject.contentType,
    quarantined: fileObject.quarantined,
  };
}

type SuccessPageProp = {
  transfer: Transfer;
};

type CardProp = SuccessPageProp;

type UserDetailsProp = {
  name?: string;
  email?: string;
};

type ValidityProp = {
  expirationTime?: Date;
};

type AllFilesDetailsProp = {
  totalFilesCount?: number;
  totalFilesSize?: number;
  isTransferQuarantined: boolean;
};

type FileRowProp = DownloadFileProp & {
  fileSize: number;
  url: URL;
  mimeType?: string;
  malwareStatus: MalwareStatus;
  malwareText?: string;
  quarantined?: boolean;
  isV1Transfer: boolean;
  fileIndex: number;
};

type FileDetailsProp = {
  fileName?: string;
  fileSize?: number;
  malwareStatus: MalwareStatus;
  quarantined?: boolean;
};

type SenderDetailsProp = {
  senderEmail?: string;
  senderName?: string;
  senderUid?: string;
};

type CardHeaderProp = SenderDetailsProp & {
  // Sender sent transfer via skp-light instead of
  // skp-client.
  //
  // At the moment, we have no way of figuring
  // out if the name & email entered by sender in skp-light's form
  // are legitmate or not. Therefore, sender who sends transfer
  // via skp-light are catagorised as anonymous sender.
  isSenderAnonymous: boolean;
  expirationTime?: Date;
  totalFileSize?: number;
  totalFileCount?: number;
  isTransferQuarantined: boolean;
  transferType: TransferType;
};

/**
 * What the prominent download button offers, plus the data that flavour needs.
 *
 * The single-file variant is the policy's own variant, so the file it points at
 * stays in lockstep with `resolveTransferDownloadCta`. The ZIP variant carries
 * the quarantine state the ZIP endpoint needs, which is page state rather than
 * policy output. Keeping the two payloads on their own variants means neither
 * flavour can be handed the other's props.
 */
type TransferDownloadCtaProp =
  | Extract<TransferDownloadCta, { kind: "singleFile" }>
  | {
      kind: "zip";
      transferRecipientId: string;
      isTransferQuarantined: boolean;
      quarantineStats: QuarantineStats;
    };

type TransferDownloadButtonProp = {
  cta: TransferDownloadCtaProp;
  /**
   * Colours the button red, the same signal an unquarantined but flagged file
   * row carries. Shared by both flavours.
   */
  hasUnquarantinedInfectedFile: boolean;
};

type PreviewButtonProp = {
  mimeType?: string;
  transferRecipientId: string;
  objectUid: string;
  fileName: string;
  fileUrl: URL;
  isV1Transfer: boolean;
  fileIndex: number;
  disabled?: boolean;
  onClick: (data: URL) => void;
};

type PreviewIconProp = {
  color: string;
  height: number;
  width: number;
};

type CalendarIconProp = PreviewIconProp;

/**
 * Returns content for transfer page.
 *
 * @param contentProp
 * @returns JSX element represeting content.
 */
export default function SuccessPage(contentProp: SuccessPageProp): JSX.Element {
  // Transfer uses anonAddress/anonName for anonymous senders
  // and senderAddress/senderName for registered senders
  const isSenderAnonymous: boolean =
    !!contentProp.transfer.anonAddress && !!contentProp.transfer.anonName;

  const senderName = isSenderAnonymous
    ? contentProp.transfer.anonName
    : contentProp.transfer.senderName ?? "--";

  const senderEmail = isSenderAnonymous
    ? contentProp.transfer.anonAddress
    : contentProp.transfer.senderAddress ?? "--";

  const senderUid = isSenderAnonymous
    ? undefined
    : contentProp.transfer.senderUid ?? undefined;

  // For Transfer, we check if senderUid exists to determine if mailbox exists
  const senderMailboxExists: boolean = !!contentProp.transfer.senderUid;

  return (
    <div className="flex w-full flex-col items-center">
      <Card transfer={contentProp.transfer} />

      {!isSenderAnonymous &&
        senderMailboxExists &&
        senderEmail !== contentProp.transfer.recipientAddress && (
          <div className="mb-[60px] w-full max-w-[760px]">
            <ReplyToMailbox
              senderName={senderName}
              uid={senderUid ?? undefined}
              onGoToMailbox={async () => {
                const senderUid = contentProp.transfer.senderUid;

                if (!senderUid) return;

                const recipientName = contentProp.transfer.recipientName ?? "";

                // If recipient doesnt have teambeam account
                // then its name is set as their email.
                // in this case, we dont prefill name.
                const emailMatch = recipientName.match(
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                );

                const portalOrigin = getSenderMailboxOriginFromTransfer(
                  contentProp.transfer
                );

                const payload: FieldsPrefillData = {
                  type: FormPrefillType.replyPrefill,
                  mailboxUid: senderUid,
                  name: emailMatch ? "" : contentProp.transfer.recipientName,
                  email: contentProp.transfer.recipientAddress,
                };

                const encoded = encodeBase64UrlJson(payload);

                const url = new URL(`/mailbox/${senderUid}`, portalOrigin);

                url.hash = `${constants.replyHashParam}=${encoded}`;

                window.location.assign(url.toString());
              }}
            />
          </div>
        )}
      <Spacer />
    </div>
  );
}

/**
 * Represents a horizontal line.
 * @returns a JSX element representing a horizontal line.
 */
function Divider() {
  return <hr className="h-[1px] bg-[#DBDBDB]"></hr>;
}

/**
 * Contains the main content of the page.
 * @returns a JSX element representing card.
 */
function Card(cardProp: CardProp) {
  // A transfer is "quarantined" (its ZIP excludes files, button reads
  // "Download approved files") only when at least one object was actually
  // quarantined by the backend — not merely because the scan was non-OK.
  const isTransferQuarantined = cardProp.transfer.objects.some(
    (obj) => obj.quarantined
  );

  const isSenderAnonymous =
    !!cardProp.transfer.anonAddress && !!cardProp.transfer.anonName;

  const files: SkpTransferFile[] = cardProp.transfer.objects.map((obj) =>
    mapTransferFileObjectToSkpTransferFile(obj)
  );

  const isDropTransfer = cardProp.transfer.type === TransferType.drop;
  const cardBackgroundColor = isDropTransfer ? "bg-white" : "bg-[#F7F7F7]";

  return (
    <div
      className={`mb-5 w-screen max-w-[760px] ${cardBackgroundColor} pb-12 sm:my-20 sm:rounded-xl`}
      style={{
        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.12)",
      }}
    >
      <CardHeader
        isSenderAnonymous={isSenderAnonymous}
        expirationTime={cardProp.transfer.expiresAt}
        senderEmail={
          isSenderAnonymous
            ? cardProp.transfer.anonAddress
            : cardProp.transfer.senderAddress ?? "--"
        }
        senderName={
          isSenderAnonymous
            ? cardProp.transfer.anonName
            : cardProp.transfer.senderName ?? "--"
        }
        senderUid={
          isSenderAnonymous
            ? undefined
            : cardProp.transfer.senderUid ?? undefined
        }
        totalFileCount={files.length}
        totalFileSize={cardProp.transfer.totalSize}
        isTransferQuarantined={isTransferQuarantined}
        transferType={cardProp.transfer.type}
      />

      {cardProp.transfer.type !== TransferType.drop && (
        <SubjectAndMessage
          subject={cardProp.transfer.subject ?? ""}
          message={cardProp.transfer.message ?? ""}
          signature={cardProp.transfer.signature}
        />
      )}
      <AttactedFilesSection
        attachedFiles={files}
        isTransferQuarantined={isTransferQuarantined}
        transferRecipientId={cardProp.transfer.uid}
        transfer={cardProp.transfer}
      />
    </div>
  );
}

type AttactedFilesSectionProp = {
  isTransferQuarantined: boolean;
  attachedFiles?: SkpTransferFile[];
  transferRecipientId: string;
  transfer: Transfer;
};

const AttactedFilesSection = ({
  attachedFiles,
  isTransferQuarantined,
  transferRecipientId,
  transfer,
}: AttactedFilesSectionProp) => {
  const hasAttachedFiles = (attachedFiles?.length ?? 0) > 0;
  const downloadCta = resolveTransferDownloadCta(attachedFiles);
  const isNoFilesCardRedesignEnabled =
    featureFlags.noFilesTransfer && !hasAttachedFiles;

  const quarantineStats = useMemo<QuarantineStats>(() => {
    if (!attachedFiles)
      return { infectedCount: 0, errorCount: 0, unknownCount: 0 };
    return attachedFiles.reduce(
      (acc, file) => {
        // Only quarantined files are excluded from the ZIP, so only they
        // should be reported as "removed" and counted for the footer notice.
        if (!file.quarantined) return acc;
        const status = file.malware.status;
        if (status === MalwareStatus.infected) acc.infectedCount++;
        else if (status === MalwareStatus.error) acc.errorCount++;
        else if (status === MalwareStatus.unknown) acc.unknownCount++;
        return acc;
      },
      { infectedCount: 0, errorCount: 0, unknownCount: 0 } as QuarantineStats
    );
  }, [attachedFiles]);

  const hasUnquarantinedInfectedFile = useMemo(
    () =>
      attachedFiles?.some(
        (f) => f.malware.status === MalwareStatus.infected && !f.quarantined
      ) ?? false,
    [attachedFiles]
  );

  const hasInfectedFile = quarantineStats.infectedCount > 0;
  const hasScanIncomplete =
    quarantineStats.errorCount > 0 || quarantineStats.unknownCount > 0;

  return (
    <div className="px-4 pt-10 sm:px-16 sm:py-0 sm:pt-10">
      {
        // A transfer with a single file offers that file directly instead of a
        // ZIP the recipient would have to unpack. skp-client follows the same
        // rule, see skp-client#977.
        downloadCta.kind !== "none" && (
          <TransferDownloadButton
            cta={
              downloadCta.kind === "singleFile"
                ? downloadCta
                : {
                    kind: "zip",
                    transferRecipientId,
                    isTransferQuarantined,
                    quarantineStats,
                  }
            }
            hasUnquarantinedInfectedFile={hasUnquarantinedInfectedFile}
          />
        )
      }
      <div
        className={isNoFilesCardRedesignEnabled ? "h-[28px]" : "h-6 sm:h-9"}
      />
      <div className={isNoFilesCardRedesignEnabled ? "pb-[28px]" : "pb-3"}>
        {hasAttachedFiles ? (
          <>
            <Divider />
            {attachedFiles?.map((file, fileIndex) => (
              <React.Fragment key={file.objectId}>
                <FileRow
                  mimeType={file.mimeType}
                  url={file.url}
                  fileName={file.name}
                  fileSize={file.size}
                  objectUid={file.objectId}
                  transferRecipientId={transferRecipientId!}
                  malwareStatus={file.malware.status}
                  malwareText={file.malware.text}
                  quarantined={file.quarantined}
                  isV1Transfer={transfer.type === TransferType.archive}
                  fileIndex={fileIndex}
                />
                <Divider />
              </React.Fragment>
            ))}
          </>
        ) : isNoFilesCardRedesignEnabled ? (
          <NoAttachedFilesState />
        ) : (
          <Divider />
        )}
      </div>
      <CardFooter
        hasAttachedFiles={hasAttachedFiles}
        accessTimestamp={transfer.accessedAt}
        expirationTimestamp={transfer.expiresAt}
        malwareStatus={transfer.malwareStatus}
        uploadTimestamp={transfer.uploadedAt}
        hasInfectedFile={hasInfectedFile}
        hasScanIncomplete={hasScanIncomplete}
      />
    </div>
  );
};

function NoAttachedFilesState() {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center text-[#A3A3A3]">
      <div className="flex items-center gap-3">
        <FontAwesomeIcon
          icon={faBoxOpen as IconProp}
          className="text-[30px]"
          aria-hidden
        />
        <span className="text-[20px] font-normal leading-none">
          {t("This transfer contains no files")}
        </span>
      </div>
    </div>
  );
}

type SubjectAndMessageProp = {
  subject: string;
  message: string;
  signature: string | null;
};

const SubjectAndMessage = ({
  subject,
  message,
  signature,
}: SubjectAndMessageProp) => {
  const { t } = useTranslation();
  const hasSubject = subject && subject.trim().length > 0;
  const displaySubject = hasSubject ? subject : t("No Subject");
  const subjectColor = hasSubject ? undefined : "#8E8E8E";

  return (
    <div className="bg-white px-4 py-12 text-[15px] sm:px-16">
      <div
        className="font-open-sans-semibold leading-5"
        style={subjectColor ? { color: subjectColor } : undefined}
      >
        {displaySubject}
      </div>
      <div className="h-5" />
      <div className="whitespace-pre-wrap leading-[19px]">{message}</div>
      <div className="h-5" />
      {signature && (
        <>
          <Divider />
          <div className="h-2" />
          <div className="whitespace-pre-wrap text-[13px] text-[#747474]">
            {signature}
          </div>
        </>
      )}
    </div>
  );
};

type DownloadProp = {
  downloadURL: URL;
  wasErrorEncounteredRef: MutableRefObject<boolean>;
  onErrorEncountered: (error: Error | null) => void;
  event: React.MouseEvent<HTMLAnchorElement>;
  i18n: i18n;
};

const downloadFile = ({
  downloadURL,
  event,
  i18n,
  onErrorEncountered,
  wasErrorEncounteredRef,
}: DownloadProp) => {
  event.preventDefault();
  fetch(downloadURL, { method: "HEAD" }).then((response) => {
    if (wasErrorEncounteredRef.current) {
      wasErrorEncounteredRef.current = false;
      return;
    }

    if (response.ok) {
      window.location.href = downloadURL.href;
    } else {
      let userFacingError: Error = new Error();
      userFacingError.name = i18n.t(`Something went wrong`);
      userFacingError.message = i18n.t(
        `The file could not be downloaded. There was a problem accessing the file.`
      );
      wasErrorEncounteredRef.current = true;
      onErrorEncountered(userFacingError);
    }
  });
};

/**
 * Consists of file details & action buttons for previewing and downloading
 * the file.
 * @returns JSX element representing row.
 */
function FileRow(fileRowProp: FileRowProp) {
  const { t, i18n } = useTranslation();
  const { isMobileWidth } = useWindowDimensions();
  const [error, setError] = useState<Error | null>(null);
  const wasErrorEncounteredRef = useRef<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const filePreviewUrlRef = useRef<URL | undefined>(undefined);

  // Whether the file may be downloaded is decided exclusively by the backend
  // `quarantined` flag. The malware status only drives the informational
  // label/tooltip explaining *why* a quarantined file is blocked: a file may
  // have a non-OK malware status yet still be downloadable when the admin chose
  // not to quarantine that status.
  const quarantined = fileRowProp.quarantined === true;
  const isInfected = fileRowProp.malwareStatus === MalwareStatus.infected;

  const quarantineLabel = !quarantined
    ? null
    : isInfected
    ? fileRowProp.malwareText
      ? t(`Infected with {{malware}}`, { malware: fileRowProp.malwareText })
      : t(`Infected with unknown malware`)
    : t(`Not checked for malware`);

  const downloadURL: URL = fileRowProp.url;
  const warningColor: string = cssVar("--warning-color");
  const labelColor = isInfected ? warningColor : "#1A1A1A";

  const row = (
    <div className="flex h-12 flex-row items-center py-[10px]">
      <FileIcon
        mimeType={fileRowProp.mimeType}
        fileName={fileRowProp.fileName}
      />
      <div className="w-3" />
      <div className="flex grow flex-row items-center gap-[10px]">
        <div className={quarantined ? "overflow-hidden" : "w-full"}>
          <FileDetails
            fileName={fileRowProp.fileName}
            fileSize={fileRowProp.fileSize}
            malwareStatus={fileRowProp.malwareStatus}
            quarantined={quarantined}
          />
        </div>
        {quarantineLabel && (
          <div
            className="shrink-0 self-start text-xs font-medium"
            style={{ color: labelColor }}
          >
            {quarantineLabel}
          </div>
        )}
      </div>
      <Spacer />
      <div className="mr-3">
        <PreviewButton
          objectUid={fileRowProp.objectUid}
          fileName={fileRowProp.fileName}
          mimeType={fileRowProp.mimeType}
          transferRecipientId={fileRowProp.transferRecipientId}
          fileUrl={fileRowProp.url}
          isV1Transfer={fileRowProp.isV1Transfer}
          fileIndex={fileRowProp.fileIndex}
          disabled={quarantined}
          onClick={(filePreviewUrl) => {
            filePreviewUrlRef.current = filePreviewUrl;
            setShowPreview(true);
          }}
        />
        {showPreview && (
          <PreviewPage
            objectUid={fileRowProp.objectUid}
            fileName={fileRowProp.fileName}
            mimeType={fileRowProp.mimeType}
            filePreviewUrl={filePreviewUrlRef.current}
            transferRecipientId={fileRowProp.transferRecipientId}
            fileUrl={fileRowProp.url}
            onClose={() => setShowPreview(false)}
          />
        )}
      </div>
      {quarantined ? (
        <IconButton disabled sx={{ height: "40px", width: "40px" }}>
          <FontAwesomeIcon
            icon={faArrowDownToLine as IconProp}
            className="text-[21px]"
          />
        </IconButton>
      ) : (
        <Tooltip
          title={t(`Download {{fileName}}`, { fileName: fileRowProp.fileName })}
        >
          <a
            href={downloadURL.href}
            onClick={(event) =>
              downloadFile({
                event: event,
                downloadURL: downloadURL,
                i18n: i18n,
                onErrorEncountered: setError,
                wasErrorEncounteredRef: wasErrorEncounteredRef,
              })
            }
          >
            <IconButton sx={{ height: "40px", width: "40px" }}>
              <FontAwesomeIcon
                icon={faArrowDownToLine as IconProp}
                style={{ color: "#1A1A1A" }}
                className="text-[21px]"
              />
            </IconButton>
          </a>
        </Tooltip>
      )}
      {error && (
        <ExceptionDialog
          title={error.name}
          description={`${error.message}`}
          onDismiss={() => setError(null)}
        />
      )}
    </div>
  );

  if (quarantined) {
    const quarantineReason = getFileQuarantineTooltip(
      fileRowProp.malwareStatus,
      t
    );

    if (isMobileWidth) {
      return (
        <div>
          {row}
          <div
            className="pb-1 pl-[45px] text-[10px]"
            style={{ color: labelColor }}
          >
            {quarantineReason}
          </div>
        </div>
      );
    }

    return (
      <Tooltip title={quarantineReason} placement="top" followCursor>
        {row}
      </Tooltip>
    );
  }

  return row;
}

/**
 * Prominent call-to-action for downloading the current transfer.
 *
 * Comes in the two flavours `cta` discriminates: `zip` downloads the whole
 * transfer as one archive, `singleFile` downloads that file as-is. Both share
 * the same styling and error handling, only the label, the icon and the URL
 * differ.
 *
 * @returns a JSX element representing a Floating action button.
 */
function TransferDownloadButton({
  cta,
  hasUnquarantinedInfectedFile,
}: TransferDownloadButtonProp) {
  const { t, i18n } = useTranslation();
  const { isMobileWidth } = useWindowDimensions();
  const [error, setError] = useState<Error | null>(null);
  const wasErrorEncounteredRef = useRef<boolean>(false);
  const buttonColor = hasUnquarantinedInfectedFile
    ? cssVar("--warning-color")
    : cssVar("--primary-color");

  const downloadUrl =
    cta.kind === "singleFile"
      ? cta.file.url
      : getTransferDownloadUrl(cta.transferRecipientId);

  const triggerDownload = (event: React.MouseEvent<HTMLAnchorElement>) =>
    downloadFile({
      event,
      downloadURL: downloadUrl,
      i18n,
      onErrorEncountered: setError,
      wasErrorEncounteredRef,
    });

  const button = (
    <a href={downloadUrl.href} download="" onClick={triggerDownload}>
      <Button
        type="button"
        variant="contained"
        sx={{
          backgroundColor: buttonColor,
          color: "#FFFFFF",
          borderRadius: 18,
          ":hover": {
            backgroundColor: buttonColor,
            filter: "brightness(0.95)",
          },
        }}
      >
        {cta.kind === "singleFile" ? (
          <FontAwesomeIcon
            icon={faArrowDownToLine as IconProp}
            style={{ color: "#FFFFFF" }}
            className="text-[18px]"
          />
        ) : (
          <DownloadAllButtonIcon
            color="#FFFFFF"
            height={18.767}
            width={15.796}
          />
        )}
        <div className="ml-2">
          {cta.kind === "singleFile"
            ? t(`Download file`)
            : cta.isTransferQuarantined
            ? t(`Download approved files`)
            : t(`Download all`)}
        </div>
      </Button>
      {error && (
        <ExceptionDialog
          title={error.name}
          description={error.message}
          onDismiss={() => {
            setError(null);
          }}
        />
      )}
    </a>
  );

  if (cta.kind === "singleFile") {
    return (
      <Tooltip title={t(`Download {{fileName}}`, { fileName: cta.file.name })}>
        {button}
      </Tooltip>
    );
  }

  const zipTooltip = getZipTooltip(cta.quarantineStats, t);

  return (
    <>
      {cta.isTransferQuarantined && !isMobileWidth ? (
        <Tooltip title={zipTooltip}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      {cta.isTransferQuarantined && isMobileWidth && zipTooltip && (
        <div
          className="mt-1 text-[10px]"
          style={{ color: cssVar("--warning-color") }}
        >
          {zipTooltip}
        </div>
      )}
    </>
  );
}

/**
 * Contains file name and it's size.
 * @returns a JSX element representing file details.
 */
function FileDetails(fileDetailsProp: FileDetailsProp) {
  const { isMobileWidth } = useWindowDimensions();
  // Greying-out and the narrower layout (to make room for the status label)
  // apply only to files the backend actually quarantined.
  const quarantined = fileDetailsProp.quarantined === true;
  const isInfected = fileDetailsProp.malwareStatus === MalwareStatus.infected;
  const isBlocked = quarantined;
  const maxFileDetailsWidth = isBlocked
    ? isMobileWidth
      ? `max-w-[68px]`
      : `max-w-[190px]`
    : isMobileWidth
    ? `max-w-[180px]`
    : `max-w-[500px]`;
  const fileNameColor = !quarantined
    ? ""
    : isInfected
    ? cssVar("--warning-color")
    : "#999999";
  const fileSizeColor =
    quarantined && isInfected ? cssVar("--warning-color") : "#747474";
  return (
    <div className={`flex flex-col`}>
      <div
        className={`${maxFileDetailsWidth} truncate text-xs`}
        style={{
          color: fileNameColor,
        }}
      >
        {fileDetailsProp.fileName ?? ""}
      </div>
      <div className="text-[10px]" style={{ color: fileSizeColor }}>
        {getHumanReadableFileSize(fileDetailsProp.fileSize ?? 0)}
      </div>
    </div>
  );
}

/**
 * Consists of user avatar & its details.
 * @returns a JSX element representing the header of the card.
 */
function CardHeader({
  isSenderAnonymous,
  isTransferQuarantined,
  expirationTime,
  senderEmail,
  senderName,
  senderUid,
  totalFileCount,
  totalFileSize,
  transferType,
}: CardHeaderProp) {
  const { isMobileWidth } = useWindowDimensions();
  const avatarSize: number = !isMobileWidth ? 60 : 40;
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center bg-[#F7F7F7] px-[30px] py-3 sm:h-44 sm:rounded-t-xl sm:py-5">
      <Avatar
        isAnonymous={isSenderAnonymous}
        uid={senderUid}
        name={senderName}
        size={avatarSize}
        transferType={transferType}
      />
      <div className="h-0.5 sm:h-2" />
      <div className="flex flex-col">
        <div className="flex items-center leading-[22px]">
          {transferType === TransferType.p2p ? (
            <UserDetails name={senderName} email={senderEmail} />
          ) : transferType === TransferType.mailbox ? (
            <div className="font-open-sans flex flex-col items-center">
              <div className="flex min-w-0 max-w-[500px] items-center">
                <span className="shrink-0">
                  {t("Uploaded via mailbox by")}&nbsp;
                </span>
                <Tooltip title={senderName}>
                  <span className="min-w-0 truncate">{senderName}</span>
                </Tooltip>
              </div>
              <Tooltip title={senderEmail}>
                <div className="max-w-[650px] truncate">({senderEmail})</div>
              </Tooltip>
            </div>
          ) : transferType === TransferType.drop ? (
            <div className="font-open-sans text-center">
              {t("Download link generated via TeamBeam")}
            </div>
          ) : (
            <UserDetails name={senderName} email={senderEmail} />
          )}
        </div>
      </div>
      <div className="h-1.5" />
      <div className="flex items-center justify-center gap-x-4">
        <AllFilesDetails
          totalFilesCount={totalFileCount}
          totalFilesSize={totalFileSize}
          isTransferQuarantined={isTransferQuarantined}
        />
        <Validity expirationTime={expirationTime} />
      </div>
    </div>
  );
}

/**
 *  Consists of user's name & email.
 * @returns a JSX element representing user's details.
 */
function UserDetails(userDetailsProp: UserDetailsProp) {
  return (
    <div className="font-open-sans flex max-w-[700px] text-sm sm:text-lg">
      <div className="truncate font-medium">{userDetailsProp.name}&nbsp;</div>
      <div className="truncate">({userDetailsProp.email})</div>
    </div>
  );
}

/**
 * Consists of total number of files & their size.
 * @returns a JSX element representing details of all files.
 */
function AllFilesDetails(allFilesDetailsProp: AllFilesDetailsProp) {
  const { t } = useTranslation();
  const totalFiles = allFilesDetailsProp?.totalFilesCount ?? 0;
  const filesDetailsColor = allFilesDetailsProp.isTransferQuarantined
    ? cssVar("--warning-color")
    : `#747474`;

  return (
    <div className="flex flex-row items-center gap-x-[5px]">
      <FontAwesomeIcon
        icon={faFile as IconProp}
        className={`text-[12px] text-[#747474]`}
      />
      <div
        className={`flex flex-row gap-x-0.5 text-[10px]`}
        style={{ color: filesDetailsColor }}
      >
        <div>
          {totalFiles} {totalFiles > 1 ? t(`Files`) : t(`File`)}
        </div>
        <div>
          ({getHumanReadableFileSize(allFilesDetailsProp?.totalFilesSize ?? 0)})
        </div>
      </div>
    </div>
  );
}

/**
 * Contains the expiry date of the transfer.
 * @returns a JSX element representing the validity of the transfer.
 */
function Validity({ expirationTime }: ValidityProp) {
  const { t } = useTranslation();
  const date = expirationTime?.getDate();
  const paddedDate = padIfSingleDigit(date);

  const year = expirationTime?.getFullYear();

  const month = expirationTime?.getMonth();
  const paddedMonth = padTransferExpirationMonth(month);

  const validity: string =
    paddedDate && paddedMonth && year
      ? `${paddedDate}.${paddedMonth}.${year}`
      : `-`;
  return (
    <div className="flex flex-row items-center gap-x-[5px]">
      <CalendarIcon color="#747474" height={11.5} width={10.5} />
      <div className="flex flex-row gap-x-0.5 text-[10px] text-[#747474]">
        <div>{t(`Valid until`)}</div>
        <div>{validity}</div>
      </div>
    </div>
  );
}

function PreviewButton({
  transferRecipientId,
  fileName,
  objectUid,
  mimeType,
  fileUrl,
  isV1Transfer,
  fileIndex,
  disabled,
  onClick,
}: PreviewButtonProp) {
  const { t } = useTranslation();

  if (disabled) {
    return (
      <span>
        <IconButton disabled>
          <PreviewIcon color="#9E9E9E" height={14} width={16} />
        </IconButton>
      </span>
    );
  }

  return (
    <Tooltip title={t(`Preview {{fileName}}`, { fileName: fileName })}>
      <IconButton
        onClick={async () => {
          let result: URL;
          if (
            mimeType?.startsWith("video") ||
            mimeType?.endsWith("pdf") ||
            mimeType?.startsWith("audio")
          ) {
            // Embed preview: use the file's own URL (v1 or v2) with ?embed
            const embedUrl = new URL(fileUrl.href);
            embedUrl.search = "embed";
            result = embedUrl;
          } else {
            // Image preview: v1 uses index-based endpoint, v2 uses objectUid
            result = isV1Transfer
              ? getFilePreviewUrlV1(transferRecipientId, fileIndex)
              : getFilePreviewUrl({ objectUid, transferRecipientId });
          }
          onClick(result);
        }}
      >
        <PreviewIcon color="#1A1A1A" height={14} width={16} />
      </IconButton>
    </Tooltip>
  );
}

type CardFooterProp = {
  hasAttachedFiles: boolean;
  malwareStatus: MalwareStatus | undefined;
  accessTimestamp: Date | null | undefined;
  uploadTimestamp: Date | undefined;
  expirationTimestamp: Date | undefined;
  hasInfectedFile: boolean;
  hasScanIncomplete: boolean;
};

/**
 * Card footer component displaying transfer information including
 * malware scan status, access timestamp, upload timestamp, and expiration timestamp.
 */
function CardFooter({
  hasAttachedFiles,
  malwareStatus,
  accessTimestamp,
  expirationTimestamp,
  uploadTimestamp,
  hasInfectedFile,
  hasScanIncomplete,
}: CardFooterProp) {
  const isNoFilesTransfer = featureFlags.noFilesTransfer && !hasAttachedFiles;
  const { t } = useTranslation();
  const environment = useSkpEnvironment();
  // timeZone from environment is in IANA format (e.g., "Europe/Berlin")
  // formatDateTime uses Intl.DateTimeFormat which supports IANA timezone identifiers
  const timezone = environment?.timeZone ?? "UTC";

  // Malware scan status text
  let footerTextMalware: string | JSX.Element = "";
  const warningColor = cssVar("--warning-color");
  if (malwareStatus !== undefined && !isNoFilesTransfer) {
    switch (malwareStatus) {
      case MalwareStatus.ok:
        footerTextMalware = t(
          "This transfer has been scanned for malware; no infected files were detected."
        );
        break;
      case MalwareStatus.unknown:
        footerTextMalware = t(
          "This transfer has not been scanned for malware yet."
        );
        break;
      case MalwareStatus.error:
        footerTextMalware = t(
          "This transfer could not be scanned for malware."
        );
        break;
      case MalwareStatus.infected:
        footerTextMalware = (
          <>
            {t("This transfer was scanned for malware; ")}
            {hasInfectedFile && (
              <span style={{ color: warningColor }}>
                {t("infected files were marked in red.")}{" "}
              </span>
            )}
            {hasScanIncomplete && (
              <span>
                {t("For some files the malware scan could not be completed.")}
              </span>
            )}
          </>
        );
        break;
      default:
        footerTextMalware = "";
    }
  }

  // Access timestamp text
  let footerTextAccessed = t("The transfer has not been retrieved yet.");
  if (accessTimestamp) {
    const [dateAccess, timeAccess] = formatDateTime(accessTimestamp, timezone);
    if (dateAccess && timeAccess) {
      footerTextAccessed = t(
        "This transfer has been accessed once on {{dateAccess}}, at {{timeAccess}}.",
        {
          dateAccess,
          timeAccess,
        }
      );
    } else {
      footerTextAccessed = t("This transfer has been accessed once.");
    }
  }

  // Upload timestamp text
  let uploadTimeText = "";
  if (uploadTimestamp) {
    const [dateUpload, timeUpload] = formatDateTime(uploadTimestamp, timezone);
    if (dateUpload && timeUpload) {
      const uploadKey = isNoFilesTransfer
        ? "The transfer was uploaded on {{dateUpload}}, {{timeUpload}}"
        : "The files were uploaded on {{dateUpload}}, {{timeUpload}}";
      uploadTimeText = t(uploadKey, { dateUpload, timeUpload });
    }
  }

  // Expiration timestamp text
  let expirationTimeText = "";
  if (expirationTimestamp) {
    const [dateExpiration, timeExpiration] = formatDateTime(
      expirationTimestamp,
      timezone
    );
    if (dateExpiration && timeExpiration) {
      expirationTimeText = t(
        " and will be accessible until {{dateExpiration}}, {{timeExpiration}}.",
        {
          dateExpiration,
          timeExpiration,
        }
      );
    }
  }

  const footerParts = [
    footerTextMalware,
    footerTextAccessed,
    uploadTimeText,
    expirationTimeText,
  ].filter((part) => {
    if (typeof part === "string") {
      return part.trim() !== "";
    }
    return true;
  });

  return (
    <div className="px-0 pb-[30px] text-[10px] text-[#747474]">
      {footerParts.map((part, index) => (
        <span key={index}>
          {index > 0 && " "}
          {part}
        </span>
      ))}
    </div>
  );
}

function PreviewIcon(previewIconProp: PreviewIconProp) {
  return (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height={previewIconProp.height}
        width={previewIconProp.width}
        viewBox="0 0 576 500"
        fill={previewIconProp.color}
      >
        <path d="M288 144a110.9 110.9 0 0 0 -31.2 5 55.4 55.4 0 0 1 7.2 27 56 56 0 0 1 -56 56 55.4 55.4 0 0 1 -27-7.2A111.7 111.7 0 1 0 288 144zm284.5 97.4C518.3 135.6 410.9 64 288 64S57.7 135.6 3.5 241.4a32.4 32.4 0 0 0 0 29.2C57.7 376.4 165.1 448 288 448s230.3-71.6 284.5-177.4a32.4 32.4 0 0 0 0-29.2zM288 400c-98.7 0-189.1-55-237.9-144C98.9 167 189.3 112 288 112s189.1 55 237.9 144C477.1 345 386.7 400 288 400z" />
      </svg>
    </div>
  );
}

function CalendarIcon(calendarIconProp: CalendarIconProp) {
  return (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height={`${calendarIconProp.height}`}
        width={`${calendarIconProp.width}`}
        fill={calendarIconProp.color}
        viewBox="0 0 448 512"
      >
        <path d="M400 64h-48V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H128V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM48 96h352c8.8 0 16 7.2 16 16v48H32v-48c0-8.8 7.2-16 16-16zm352 384H48c-8.8 0-16-7.2-16-16V192h384v272c0 8.8-7.2 16-16 16zM148 320h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm96 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm96 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm-96 96h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm-96 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm192 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12z" />
      </svg>
    </div>
  );
}

type ReplyToMailboxProp = {
  senderName: string;
  uid?: string;
  onGoToMailbox: () => void;
};

function ReplyToMailbox({
  senderName,
  uid,
  onGoToMailbox,
}: ReplyToMailboxProp) {
  const { t } = useTranslation();

  return (
    <div className="flex w-5/6 flex-col items-center justify-center place-self-center px-3 sm:px-14">
      {/* Sender's avatar + name */}
      <div className="flex min-w-0 flex-col items-center justify-center gap-x-4">
        {/* we dont show this component to anonymous sender, therefore isAnonymous is set false*/}
        <Avatar uid={uid} name={senderName} size={50} isAnonymous={false} />
        <div className="h-2" />
        <div className="truncate text-[20px] leading-[20px]">{senderName}</div>
      </div>

      <div className="h-2" />

      <div className="min-w-0 flex-1 flex-col sm:flex">
        <div className="mt-1 text-[15px] leading-[19px]">
          <Trans
            parent="span"
            className="text-sm font-normal"
            i18nKey="Send data back to {{senderName}} quickly and securely. Simply select files and send them even without your own TeamBeam account."
            values={{ senderName }}
          />
        </div>

        <div className="h-4 sm:h-[10px] " />

        <div className="place-self-center">
          <Button
            variant="contained"
            onClick={onGoToMailbox}
            startIcon={
              <FontAwesomeIcon
                icon={faReply as IconProp}
                style={{
                  color: "#FFFFFF",
                  width: 13,
                  height: 13,
                  paddingBottom: 2.2,
                }}
              />
            }
          >
            {t("Answer via TeamBeam")}
          </Button>
        </div>
      </div>
    </div>
  );
}
